import { displayOrDownloadUrlDocument, displayableMime, useDownloadDocument } from "@api";
import type { IDocument } from "@bbj-types/document";
import type { ObjectId } from "@bbj-types/utils";
import { TypoTooltip } from "@data-display/wrapper/DetectOverflow";
import { IconButtonVariant } from "@inputs/button/IconButtonVariant";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Stack } from "@mui/material";
import type { Dispatch, SetStateAction } from "react";

export interface IUploadFileFieldPopoverSelectableDocumentsItemProps {
  document: IDocument;
  selectedDocuments: Set<ObjectId>;
  setSelectedDocuments: Dispatch<SetStateAction<Set<ObjectId>>>;
}

export const UploadFileFieldPopoverSelectableDocumentsItem = ({
  document,
  selectedDocuments,
  setSelectedDocuments,
}: IUploadFileFieldPopoverSelectableDocumentsItemProps) => {
  const [, getDocumentDownload] = useDownloadDocument();
  const previewDisabled = !displayableMime.includes(document.mimeType);

  const handleDocumentSelect = () => {
    setSelectedDocuments((selectedDocuments) => {
      const copySelectedDocuments = new Set(selectedDocuments);
      if (copySelectedDocuments.has(document._id)) {
        copySelectedDocuments.delete(document._id);
      } else {
        copySelectedDocuments.add(document._id);
      }
      return copySelectedDocuments;
    });
  };

  const handleDocumentPreview = async () => {
    const response = await getDocumentDownload(document._id);
    displayOrDownloadUrlDocument(response.data, "display", document.name, document.mimeType);
  };

  return (
    <Stack
      key={document._id}
      direction="row"
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <TypoTooltip variant="body1" noWrap>
        {document.name}
      </TypoTooltip>
      <Stack direction="row">
        <IconButtonVariant onClick={handleDocumentSelect}>
          {selectedDocuments.has(document._id) ? <CheckIcon color="success" /> : <AddIcon color="primary" />}
        </IconButtonVariant>
        <IconButtonVariant onClick={handleDocumentPreview} disabled={previewDisabled}>
          <VisibilityIcon color={previewDisabled ? "inherit" : "primary"} />
        </IconButtonVariant>
      </Stack>
    </Stack>
  );
};
